import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Assessment, AccountBalance, Refresh, TrendingUp, TrendingDown } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import { ReportHeader, ExportToolbar } from '../../components/finance';
import { exportToPDF, exportToExcel, formatIndianCurrency } from '../../utils/exportUtils';
import assetService, { FixedAssetRegisterReport, DeferredTaxReport } from '../../services/assetService';
import { fmtMoney, currentFinancialYear, financialYearOptions } from './assetFormat';

type ReportKey = 'register' | 'deferred-tax';

const REPORT_META: Record<ReportKey, { label: string; description: string; icon: React.ReactNode; color: string }> = {
  register: {
    label: 'Fixed Asset Register',
    description: 'Statutory gross block / depreciation / net block roll-forward for the financial year.',
    icon: <Assessment />,
    color: '#0284c7',
  },
  'deferred-tax': {
    label: 'Deferred Tax',
    description: 'Book vs tax WDV by Income Tax block, and the resulting DTA / DTL.',
    icon: <AccountBalance />,
    color: '#7c3aed',
  },
};

const ReportPlaceholder: React.FC<{ icon: React.ReactNode; hint: string }> = ({ icon, hint }) => (
  <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 6 }}>
    <Box sx={{ color: 'text.disabled', textAlign: 'center', mb: 1, '& svg': { fontSize: 44 } }}>{icon}</Box>
    <TableEmptyState title="No report generated yet" hint={hint} />
  </Paper>
);

// ---------------------------------------------------------------------------
// Fixed Asset Register
// ---------------------------------------------------------------------------

const RegisterReportView: React.FC = () => {
  const [financialYear, setFinancialYear] = useState(currentFinancialYear());
  const [report, setReport] = useState<FixedAssetRegisterReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await assetService.getFixedAssetRegister(financialYear);
      setReport(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load the fixed asset register');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => exportToPDF('asset-register-report', `Fixed_Asset_Register_${financialYear}`, 'landscape');

  const handleExportExcel = () => {
    if (!report) return;
    const data = report.lines.map((l) => ({
      assetCode: l.assetCode,
      name: l.name,
      category: l.category || '',
      grossOpening: formatIndianCurrency(l.grossOpening),
      grossAdditions: formatIndianCurrency(l.grossAdditions),
      grossDeletions: formatIndianCurrency(l.grossDeletions),
      grossClosing: formatIndianCurrency(l.grossClosing),
      depOpening: formatIndianCurrency(l.depOpening),
      depCharge: formatIndianCurrency(l.depCharge),
      depDeletions: formatIndianCurrency(l.depDeletions),
      depClosing: formatIndianCurrency(l.depClosing),
      netBlock: formatIndianCurrency(l.netBlock),
    }));
    const columns = [
      { header: 'Asset Code', key: 'assetCode' },
      { header: 'Name', key: 'name' },
      { header: 'Category', key: 'category' },
      { header: 'Gross Opening', key: 'grossOpening' },
      { header: 'Gross Additions', key: 'grossAdditions' },
      { header: 'Gross Deletions', key: 'grossDeletions' },
      { header: 'Gross Closing', key: 'grossClosing' },
      { header: 'Dep. Opening', key: 'depOpening' },
      { header: 'Dep. Charge', key: 'depCharge' },
      { header: 'Dep. Deletions', key: 'depDeletions' },
      { header: 'Dep. Closing', key: 'depClosing' },
      { header: 'Net Block', key: 'netBlock' },
    ];
    exportToExcel(data, columns, `Fixed_Asset_Register_${financialYear}`, 'Fixed Asset Register');
  };

  return (
    <>
      <FilterBar>
        <TextField
          select
          size="small"
          label="Financial Year"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {financialYearOptions(6, 1).map((fy) => (
            <MenuItem key={fy} value={fy}>
              {fy}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={fetchReport} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}>
          Generate
        </Button>
        <FilterSpacer />
        {report && <ExportToolbar onExportPDF={handleExportPDF} onExportExcel={handleExportExcel} disabled={loading} size="small" />}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Box id="asset-register-report">
          <ReportHeader
            companyName="Your Company Name"
            reportTitle="Fixed Asset Register"
            fromDate={report.startISO}
            toDate={report.endISO}
          />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard title="Gross Block (Closing)" value={fmtMoney(report.totals.grossClosing)} icon={<TrendingUp />} color="#0284c7" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard title="Accum. Depreciation (Closing)" value={fmtMoney(report.totals.depClosing)} icon={<TrendingDown />} color="#d97706" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard title="Net Block" value={fmtMoney(report.totals.netBlock)} icon={<Assessment />} color="#16a34a" />
            </Grid>
          </Grid>

          <TableSurface>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ ...headerCellSx, verticalAlign: 'bottom' }}>Asset</TableCell>
                    <TableCell rowSpan={2} sx={{ ...headerCellSx, verticalAlign: 'bottom' }}>Category</TableCell>
                    <TableCell colSpan={4} align="center" sx={{ ...headerCellSx, borderLeft: '1px solid', borderColor: 'divider' }}>
                      Gross Block
                    </TableCell>
                    <TableCell colSpan={4} align="center" sx={{ ...headerCellSx, borderLeft: '1px solid', borderColor: 'divider' }}>
                      Accumulated Depreciation
                    </TableCell>
                    <TableCell rowSpan={2} align="right" sx={{ ...headerCellSx, verticalAlign: 'bottom', borderLeft: '1px solid', borderColor: 'divider' }}>
                      Net Block
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="right" sx={{ ...headerCellSx, borderLeft: '1px solid', borderColor: 'divider' }}>Opening</TableCell>
                    <TableCell align="right" sx={headerCellSx}>Additions</TableCell>
                    <TableCell align="right" sx={headerCellSx}>Deletions</TableCell>
                    <TableCell align="right" sx={headerCellSx}>Closing</TableCell>
                    <TableCell align="right" sx={{ ...headerCellSx, borderLeft: '1px solid', borderColor: 'divider' }}>Opening</TableCell>
                    <TableCell align="right" sx={headerCellSx}>Charge</TableCell>
                    <TableCell align="right" sx={headerCellSx}>Deletions</TableCell>
                    <TableCell align="right" sx={headerCellSx}>Closing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ border: 0 }}>
                        <TableEmptyState title="No asset movement in this financial year" hint="Nothing was acquired, depreciated or disposed in this period." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    report.lines.map((l) => (
                      <TableRow key={l.assetId} sx={bodyRowSx}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.assetCode}</Typography>
                          <Typography variant="caption" color="text.secondary">{l.name}</Typography>
                        </TableCell>
                        <TableCell>{l.category || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', borderLeft: '1px solid', borderColor: 'divider' }}>{fmtMoney(l.grossOpening)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.grossAdditions)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.grossDeletions)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(l.grossClosing)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', borderLeft: '1px solid', borderColor: 'divider' }}>{fmtMoney(l.depOpening)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.depCharge)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.depDeletions)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(l.depClosing)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, borderLeft: '1px solid', borderColor: 'divider' }}>{fmtMoney(l.netBlock)}</TableCell>
                      </TableRow>
                    ))
                  )}
                  {report.lines.length > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, borderLeft: '1px solid', borderColor: 'divider' }}>{fmtMoney(report.totals.grossOpening)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(report.totals.grossAdditions)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(report.totals.grossDeletions)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(report.totals.grossClosing)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, borderLeft: '1px solid', borderColor: 'divider' }}>{fmtMoney(report.totals.depOpening)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(report.totals.depCharge)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(report.totals.depDeletions)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(report.totals.depClosing)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, borderLeft: '1px solid', borderColor: 'divider' }}>{fmtMoney(report.totals.netBlock)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      ) : (
        <ReportPlaceholder icon={<Assessment />} hint="Choose a financial year above and click Generate." />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Deferred Tax
// ---------------------------------------------------------------------------

const DTA_DTL_TINT: Record<'DTA' | 'DTL' | 'NONE', string> = {
  DTA: '#0284c7',
  DTL: '#d97706',
  NONE: '#64748b',
};

const DTA_DTL_LABEL: Record<'DTA' | 'DTL' | 'NONE', string> = {
  DTA: 'Deferred Tax Asset',
  DTL: 'Deferred Tax Liability',
  NONE: 'No Difference',
};

const DeferredTaxReportView: React.FC = () => {
  const [financialYear, setFinancialYear] = useState(currentFinancialYear());
  const [report, setReport] = useState<DeferredTaxReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await assetService.getDeferredTaxReport(financialYear);
      setReport(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load the deferred tax report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => exportToPDF('deferred-tax-report', `Deferred_Tax_${financialYear}`, 'portrait');

  const handleExportExcel = () => {
    if (!report) return;
    const data = report.lines.map((l) => ({
      block: l.blockName,
      bookWdv: formatIndianCurrency(l.bookWdv),
      taxWdv: formatIndianCurrency(l.taxWdv),
      timingDifference: formatIndianCurrency(l.timingDifference),
      type: DTA_DTL_LABEL[l.type],
      amount: formatIndianCurrency(l.amount),
    }));
    const columns = [
      { header: 'Block', key: 'block' },
      { header: 'Book WDV', key: 'bookWdv' },
      { header: 'Tax WDV', key: 'taxWdv' },
      { header: 'Timing Difference', key: 'timingDifference' },
      { header: 'Type', key: 'type' },
      { header: 'Amount', key: 'amount' },
    ];
    exportToExcel(data, columns, `Deferred_Tax_${financialYear}`, 'Deferred Tax');
  };

  return (
    <>
      <FilterBar>
        <TextField
          select
          size="small"
          label="Financial Year"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {financialYearOptions(6, 1).map((fy) => (
            <MenuItem key={fy} value={fy}>
              {fy}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={fetchReport} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}>
          Generate
        </Button>
        <FilterSpacer />
        {report && <ExportToolbar onExportPDF={handleExportPDF} onExportExcel={handleExportExcel} disabled={loading} size="small" />}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Box id="deferred-tax-report">
          <ReportHeader
            companyName="Your Company Name"
            reportTitle="Deferred Tax — Book vs Tax WDV"
            subtitle={`Effective tax rate ${report.ratePct}%`}
            asOnDate={undefined}
            fromDate={undefined}
            toDate={undefined}
          />

          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>How to read this</AlertTitle>
            A tax WDV <strong>below</strong> book WDV means more depreciation has already been claimed for tax than
            for the books — tax relief already taken — which is a <strong>Deferred Tax Liability</strong> (it reverses
            as extra tax in later years). The reverse — tax WDV <strong>above</strong> book WDV — is a{' '}
            <strong>Deferred Tax Asset</strong>.
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard title="Total Deferred Tax Asset" value={fmtMoney(report.totalDTA)} icon={<TrendingUp />} color="#0284c7" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard title="Total Deferred Tax Liability" value={fmtMoney(report.totalDTL)} icon={<TrendingDown />} color="#d97706" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <StatCard title="Net (DTA − DTL)" value={fmtMoney(report.net)} icon={<Assessment />} color={report.net >= 0 ? '#16a34a' : '#dc2626'} />
            </Grid>
          </Grid>

          <TableSurface>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Block</TableCell>
                    <TableCell sx={headerCellSx} align="right">Book WDV</TableCell>
                    <TableCell sx={headerCellSx} align="right">Tax WDV</TableCell>
                    <TableCell sx={headerCellSx} align="right">Timing Difference</TableCell>
                    <TableCell sx={headerCellSx}>Type</TableCell>
                    <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 0 }}>
                        <TableEmptyState title="No blocks to report on" hint="Define an Income Tax block and post a run first." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    report.lines.map((l) => (
                      <TableRow key={l.blockId} sx={bodyRowSx}>
                        <TableCell>{l.blockName}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.bookWdv)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.taxWdv)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(l.timingDifference)}</TableCell>
                        <TableCell>
                          <TypePill label={DTA_DTL_LABEL[l.type]} tint={DTA_DTL_TINT[l.type]} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(l.amount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      ) : (
        <ReportPlaceholder icon={<AccountBalance />} hint="Choose a financial year above and click Generate." />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const AssetReportsPage: React.FC = () => {
  const [activeReport, setActiveReport] = useState<ReportKey>('register');

  return (
    <>
      <PageHeader
        title="Asset Reports"
        subtitle="Statutory fixed asset register and the deferred tax position by Income Tax block"
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(['register', 'deferred-tax'] as ReportKey[]).map((key) => {
          const meta = REPORT_META[key];
          const active = activeReport === key;
          return (
            <Grid key={key} size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                onClick={() => setActiveReport(key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveReport(key);
                  }
                }}
                sx={{
                  p: 2,
                  height: '100%',
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: active ? meta.color : 'divider',
                  background: active ? `linear-gradient(160deg, ${meta.color}1f 0%, transparent 70%)` : 'transparent',
                  transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${meta.color}1f` },
                  '&:focus-visible': { outline: `2px solid ${meta.color}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      backgroundColor: active ? meta.color : 'action.selected',
                      color: active ? '#fff' : 'text.secondary',
                      flexShrink: 0,
                      '& svg': { fontSize: 19 },
                    }}
                  >
                    {meta.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: active ? meta.color : 'text.primary' }}>{meta.label}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {meta.description}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {activeReport === 'register' ? <RegisterReportView /> : <DeferredTaxReportView />}
    </>
  );
};

export default AssetReportsPage;
